// 3D Viewer Color Constants
// Note: These use 0x hex format for Three.js compatibility
// Corresponding CSS variables are in _theme.scss

export const WHITE = 0xffffff; // --color-white
export const BLACK = 0x000000; // --color-black
export const POINT_COLOR = 0x1fdb9b; // --color-map-green
export const SELECTED_POINT_COLOR = 0x0f6d4d;
export const MIDPOINT_COLOR = 0x23e7a5; // --color-gree300
export const MEASUREMENT_POINT_COLOR = 0x0a6043;
export const COORDINTATE_LINE_COLOR_X = 0xd1d8f9;
export const COORDINTATE_LINE_COLOR_Y = 0xb3f5ed;
export const ZOOM_POINT_COLOR = 0x099483; // --color-primary
export const RESTRICTED_AREA_COLOR = 0xe43f32;
export const MOUNTING_SURFACE_COLOR = 0x7e8884;
export const SOLAR_POINT_COLOR = 0xe8e9e9;
export const MARGIN_COLOR = 0x6cb0ff;
export const SELECTED_LINE_COLOR = 0xffdd79;
export const OUTLINR_COLOR = 0xfff5d4;

export const ZOOM_FACTOR = -30;

export const SOLAR_LABEL_PIXEL_OFFSET = 24;

export const VERTICAL = "vertical";
export const HORIZONTAL = "horizontal";
export const SOLAR_GROUP = "SOLAR_GROUP";
export const AREA = "AREA";
export const AVAILABLE_LOCALES = ["de", "en"];

export const MIN_SCALE = 1;
export const MAX_SCALE = 4;
export const ZOOM_SPEED = 0.1;

// How far behind the model an annotation may sit and still be drawn, in metres.
//
// Annotations are depth-tested against the roof so that anything on the far side
// of it is hidden. Testing on the raw depth alone is unusable here: the model is
// a photogrammetry mesh, and measurement linework is built from points raycast
// onto that mesh, so it is coplanar with a surface that ripples by centimetres.
// Every annotation is therefore shifted this far toward the camera for the depth
// test only (see `@/utils/annotation-depth`). Since every annotation gets the
// same shift, it cancels out between them and only buys slack against the model.
//
// The value has to clear two things at once: the mesh's own noise, and the 85 mm
// a flush panel stands off the roof (50 mm offset + 35 mm thickness), so that a
// measurement drawn across an array still reads over the panels. Raising it past
// roughly a quarter of a metre starts letting annotations show through thin
// structures such as a parapet or a dormer cheek.
export const ANNOTATION_DEPTH_TOLERANCE_M = 0.15;

// Near clipping plane of the perspective camera, in metres.
//
// Perspective depth precision is set almost entirely by the near plane: the
// smallest depth difference the buffer can resolve at distance z is roughly
// z² / (near · 2²⁴). At the old 0.01 that was 15 mm at 50 m and 60 mm at 100 m —
// the same order as the depth biases annotations rely on, so a marker's lead over
// its own line could shrink to a handful of steps when zoomed out. At 0.25 it is
// 25 times finer.
//
// Anything closer to the camera than this is clipped, which costs nothing here:
// camera-controls keeps the camera at least `minDistance` (2 m) from its orbit
// target, and dollyToCursor keeps that target on the surface being looked at.
export const CAMERA_NEAR_M = 0.25;

// The depth shift for point markers — corners, midpoints, measurement ends.
//
// Every annotation on the same shift is compared on its true depth, and a marker
// is a small sphere centred *on* the line it marks, so on true depth it is barely
// in front of it; at viewing distance that gap is inside the depth buffer's
// precision and the line wins most of the sphere. An extra 25 cm puts every
// marker clear of the linework it sits on at any distance the viewer is used at,
// while staying far below the metres that separate a roof face from the one
// behind it — so the roof still hides a marker on the other side.
export const MARKER_DEPTH_BIAS_M = ANNOTATION_DEPTH_TOLERANCE_M + 0.25;

// Where an occluded area fill is parked. The camera renders layer 0 only, so an
// object here is neither drawn nor raycast — an area you cannot see should not be
// clickable either.
//
// Fills are culled whole rather than depth-tested per fragment, because a fill is
// the one annotation that is a *flat* approximation of a surface that is not
// flat. Testing it per fragment is a hard comparison against a roof that ripples,
// and wherever the roof happens to sit near the tolerance that comparison flips
// from pixel to pixel and frame to frame. No tolerance value fixes it — a larger
// one only moves the boundary from the skylight to the dormer — so the test is
// dropped and a line-of-sight raycast decides the whole fill instead.
export const OCCLUSION_HIDDEN_LAYER = 1;

// Lines and corner points that are only *partly* behind the model have their
// hidden portions drawn faintly instead of cut away, so an outline running
// behind a chimney reads as continuous rather than broken. Objects that take part
// sit on this layer as well as layer 0, and are drawn a second time on it by
// renderAnnotatedFrame (@/utils/annotation-depth). An area or measurement that is
// hidden as a whole is taken off this layer, so it disappears entirely as before.
export const GHOST_LAYER = 2;

// Opacity of those faint portions, relative to the line or point itself.
export const GHOST_OPACITY = 0.35;

// A segment's value label — an area edge length or a distance — goes faint when
// more than this fraction of its segment is behind the model AND the segment's
// midpoint, where the label sits, is behind it too. Both, so a label never
// fades while the stretch under it is in view, nor because a chimney covers
// just the middle of an edge that is otherwise visible.
export const SEGMENT_LABEL_FAINT_FRACTION = 0.6;

// Opacity of a faint segment label. Higher than GHOST_OPACITY: it is text on a
// pill, and at a line's 35% it gets hard to read over a busy roof.
export const FAINT_LABEL_OPACITY = 0.5;

// Depth tolerance for a measurement area's body, which is depth-tested so that
// its hidden parts can be drawn faintly like its outline's. It starts equal to
// the lines' tolerance so body and outline agree on what is hidden.
//
// The body is a flat polygon over a roof that is not flat, so wherever the roof
// sits near this tolerance above the polygon the body can mottle between full
// and faint — the same mechanism that once made the fill flicker, at much lower
// contrast now that the alternative is faint rather than absent. If that shows on
// a rippled roof, raising this moves the boundary off ordinary surface relief and
// onto genuinely tall features (chimneys, dormers, racked panels), at the cost of
// the body no longer fading under anything lower than the new value.
export const FILL_DEPTH_TOLERANCE_M = ANNOTATION_DEPTH_TOLERANCE_M;


// Slack for the raycast-based occlusion tests — labels and area fills — as
// opposed to ANNOTATION_DEPTH_TOLERANCE_M, which is the per-fragment shader
// bias for geometry. It is deliberately much looser, because the two tests are
// answering different questions.
//
// The shader bias has to be tight: it is competing with depth precision and with
// the 85 mm a flush panel stands off the roof. This one is only ever asking
// "is this thing on the other side of the building", which is a metres-scale
// question, and it is asked at points that are only approximately on the
// surface: a fill's centroid is the average of its corners, which dips below a
// convex roof, and an edge label is nudged half a metre sideways off its own
// line so the number does not sit on top of the midpoint marker. That nudge is
// horizontal, so on a 35° roof it lands the label a third of a metre under the
// surface — which at the tighter tolerance read as "hidden".
export const OCCLUSION_RAY_TOLERANCE_M = 0.5;

// Draw order *between* element types. Two objects on the same rung fall through
// to three.js' own depth sort, which is why every rung is shared by its whole
// type: a per-element tie-break here would freeze that type's ordering at
// creation time.
export const RENDERING_ORDER = {
  SOLAR_PLANE: 3,
  INNER_SOLAR_PLANE: 2,
  PANEL: 9999,
  INDIVIDUAL_PANEL: 12000,
  INDIVIDUAL_PANEL_OUTLINE: 15000,
  PANEL_TRASH: 15000,
  MEASUREMENT_PLANE: 20000,
  AUXILIARY_LINE: 25000,
  LINE: 30000,
  OUTER_POINT: 35000,
  INNER_POINT: 40000,
  // CSS2DRenderer sorts labels by renderOrder first and only falls back to
  // camera distance on a tie, so labels of a kind share one rung and let
  // distance decide between them. An area's edge lengths sit on this rung too,
  // which is why an unselected area's edge label can end up behind a nearer
  // distance label — SELECTED_LABEL_BOOST is what resolves that.
  // The x/y projection readouts, below the measurement label they belong to.
  AUXILIARY_LABEL: 44000,
  MEASUREMENT_LABEL: 45000,
  MEASUREMENT_AREA_LABEL: 50000,
  MOVE_GRID: 55000,
  ZOOM_CENTER: 60000,
};

// Added to a label's own rung for as long as its measurement is selected.
//
// A boost rather than a fixed "selected" rung, because a selection is more than
// one label: an area carries its area/angle label *and* its edge lengths, and
// lifting them all by the same amount keeps the order within the selection
// intact — the area/angle label still reads over its own edges — while putting
// the whole selection clear of everything that is not selected.
//
// 20000 puts the bottom of a selection (AUXILIARY_LABEL) above the top of
// everything unselected (MOVE_GRID), so no selected label can lose to one that
// is not selected, whatever pair happens to overlap.
export const SELECTED_LABEL_BOOST = 20000;

export const DEFAULT_HORIZONTAL_SPACING = 5;
export const DEFAULT_VERTICAL_SPACING = 5;
export const DEFAULT_MARGIN = 2;
export const DEFAULT_OFFSET = 5;

// Rack-mode constants for flat-roof mounting types.
// `null` (legacy in-memory) === Flush mode.
export const FIXED_TILT = "FIXED_TILT";
export const EAST_WEST  = "EAST_WEST";

// Backend-wire mounting mode enum — keep in sync with FFSolar-BE
// `ffsolar_be.models.mounting_configurations.MountingMode`.
export const MOUNTING_MODE = {
  FLUSH:      "FLUSH",
  FIXED_TILT: "FIXED_TILT",
  EAST_WEST:  "EAST_WEST",
};

// Bucket keys inside `area.mountingConfigurations` and the BE
// `mounting_configurations` payload. Mirrors the Pydantic field names.
export const MOUNTING_BUCKET = {
  [MOUNTING_MODE.FLUSH]:      "flush",
  [MOUNTING_MODE.FIXED_TILT]: "fixed_tilt",
  [MOUNTING_MODE.EAST_WEST]:  "east_west",
};

// In-memory `area.rackMode` (null|FIXED_TILT|EAST_WEST) ↔ wire `activeMountingMode`.
export function rackModeToMountingMode(rackMode) {
  return rackMode === null || rackMode === undefined ? MOUNTING_MODE.FLUSH : rackMode;
}
export function mountingModeToRackMode(mountingMode) {
  return mountingMode === MOUNTING_MODE.FLUSH ? null : mountingMode;
}

// Tilted-mode defaults.
export const DEFAULT_FIXED_TILT = 15;          // degrees — Fixed-tilt mode default
export const DEFAULT_EAST_WEST_TILT = 10;      // degrees — East-West mode default
export const DEFAULT_TILT = DEFAULT_FIXED_TILT; // backward-compat alias for useDefaultSolarValues
export const DEFAULT_PAIR_GAP  = 5;            // cm
export const DEFAULT_INTER_GAP = 0;            // cm — walking clearance between E-W pairs

// Panel physical thickness used for 3D rendering. Real PV modules are typically
// 30–40 mm thick; pick the middle of that range so the panels read as solid
// objects on the roof rather than zero-thickness planes.
export const PANEL_THICKNESS_M = 0.035;        // metres (35 mm)

// Sun altitude reference for inter-row spacing formula `H · sin(θ) / tan(α)`.
// 15° approximates the sun elevation at solar noon on the winter solstice
// (Dec 21) at central-European latitudes — see solar-flat-roof-master-ssot §5.2
// for the deliberate tradeoff vs. the spec's original 9 AM reference.
export const DEFAULT_SUN_ALTITUDE_DEC21_NOON = 15; // degrees

export const TEST_COMPANIES = [
  82, 101, 296, 345, 370, 483, 487, 563, 751, 1069, 1650, 2013, 2252, 2326,
  2416, 2614, 2615, 2630, 2766, 2963, 3009, 3016, 3085, 3556, 3903, 3981,
];

export const TECHNICAL_PICTURES_CATEGORY = {
  en: "Flight - Pictures - Technical",
  de: "Flug - Bilder - Technisch",
};

export const BLURRED_TECHNICAL_PICTURES_CATEGORY = {
  en: "Flight - Pictures - Blurred",
  de: "Flug - Bilder - Anonymisiert",
};


export const SENSOR_SIZE = {
  FILM_WIDTH: 36,
  FILM_HEIGHT: 24,
};

export const BLURRED_CATEGORY_INDEX = 'bl'

export const TEST_MANAUL_UPLOAD_PROJECTS = [97090, 84333, 84516]
